"use client";

import { useState, useEffect, useCallback } from "react";
import { Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  name: "Jane D.",
  restaurantType: "The Gourmet Bistro",
  position: "Owner",
  rating: 5,
  quote: "Click Viral transformed our email marketing, boosting our repeat customers significantly."
}, {
  name: "Mike S.",
  restaurantType: "Sunset Grill",
  position: "Manager",
  rating: 5,
  quote: "Their targeted campaigns brought in more reservations and increased revenue."
}, {
  name: "Sarah L.",
  restaurantType: "Urban Kitchen",
  position: "Marketing Director",
  rating: 5,
  quote: "The ROI we've seen from Click Viral's campaigns is beyond our expectations."
}, {
  name: "Carlos R.",
  restaurantType: "Coastal Seafood House",
  position: "Owner",
  rating: 5,
  quote: "Professional, data-driven approach that really understands the restaurant industry."
}, {
  name: "Emily T.",
  restaurantType: "Garden Cafe",
  position: "General Manager",
  rating: 5,
  quote: "Our email list grew by 300% in just three months with Click Viral's strategies."
}, {
  name: "David K.",
  restaurantType: "Downtown Steakhouse",
  position: "Owner",
  rating: 5,
  quote: "Finally, an email marketing partner that delivers real, measurable results for restaurants."
}];
export default function ClientTestimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const scrollNext = useCallback(() => {
    setActiveIndex(prev => (prev + 1) % testimonials.length);
  }, []);
  const scrollPrev = useCallback(() => {
    setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  }, []);
  useEffect(() => {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion || isPaused) return;
    const interval = setInterval(scrollNext, 5000);
    return () => clearInterval(interval);
  }, [isPaused, scrollNext]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        scrollPrev();
        setIsPaused(true);
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        scrollNext();
        setIsPaused(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scrollNext, scrollPrev]);
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.clientX);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const diff = e.clientX - startX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        scrollPrev();
      } else {
        scrollNext();
      }
      setIsDragging(false);
      setIsPaused(true);
    }
  };
  const handleMouseUp = () => {
    setIsDragging(false);
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const diff = e.changedTouches[0].clientX - startX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        scrollPrev();
      } else {
        scrollNext();
      }
      setIsPaused(true);
    }
  };
  return <section ref={ref} className={`py-24 px-4 bg-background transition-opacity duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl md:text-5xl font-display font-bold text-center mb-4 text-foreground">
          Client Testimonials
        </h2>
        <p className="text-center text-muted-foreground mb-16 max-w-2xl mx-auto">
          Hear from restaurant owners who've transformed their email marketing with Click Viral
        </p>

        <div className="relative max-w-5xl mx-auto">
          <div className="overflow-hidden" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
            <div className="flex transition-transform duration-[var(--duration-medium)] ease-[var(--ease-smooth)]" style={{
            transform: `translateX(-${activeIndex * 100}%)`
          }}>
              {testimonials.map((testimonial, i) => <div key={i} data-index={i} className="w-full flex-shrink-0 px-4">
                  <Card className={`transition-all duration-[var(--duration-fast)] ease-[var(--ease-out-back)] ${i === activeIndex ? "opacity-100 translate-y-0 scale-100" : "opacity-40 translate-y-2 scale-95"} hover:scale-[1.02] hover:-translate-y-1 hover:shadow-medium cursor-grab active:cursor-grabbing`} onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)}>
                    <CardContent className="p-8 md:p-12">
                      <div className="flex items-center gap-1 mb-6">
                        {[...Array(testimonial.rating)].map((_, starIdx) => <Star key={starIdx} className="w-5 h-5 fill-primary text-primary" />)}
                      </div>
                      <blockquote className="text-lg md:text-xl text-foreground mb-8 leading-relaxed">
                        "{testimonial.quote}"
                      </blockquote>
                      <div className="border-t border-border pt-6">
                        <p className="font-semibold text-foreground text-lg">
                          {testimonial.name}
                        </p>
                        <p className="text-muted-foreground">
                          {testimonial.position}, {testimonial.restaurantType}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}
            </div>
          </div>

          <div className="flex items-center justify-center gap-4 mt-8">
            <Button variant="outline" size="icon" onClick={() => {
            scrollPrev();
            setIsPaused(true);
          }} className="rounded-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-110" aria-label="Previous testimonial">
              <ChevronLeft className="w-5 h-5" />
            </Button>

            <div className="flex gap-2">
              {testimonials.map((_, i) => <Button key={i} variant="ghost" size="icon" onClick={() => {
              setActiveIndex(i);
              setIsPaused(true);
            }} className={`w-2 h-2 p-0 rounded-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${i === activeIndex ? "bg-primary w-8 hover:bg-primary" : "bg-muted hover:bg-muted-foreground/50"}`} aria-label={`Go to testimonial ${i + 1}`} />)}
            </div>

            <Button variant="outline" size="icon" onClick={() => {
            scrollNext();
            setIsPaused(true);
          }} className="rounded-full transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-110" aria-label="Next testimonial">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>;
}